import { useState } from 'react';
import type { SuggestionGroup } from '@metron/shared';
import { Button } from '../ui/button';
import { Loader2, Sparkles, X } from 'lucide-react';

export interface SuggestionBannerProps {
  suggestions: SuggestionGroup[];
  onSelectSuggestion: (sensorGroupId: number, name: string) => void;
  onDismiss: () => void;
  isLoading: boolean;
}

export function SuggestionBanner({
  suggestions,
  onSelectSuggestion,
  onDismiss,
  isLoading,
}: SuggestionBannerProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (isLoading) {
    return (
      <div className='rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950'>
        <div className='flex items-center gap-2'>
          <Loader2 className='h-4 w-4 animate-spin text-blue-600 dark:text-blue-400' />
          <span className='text-sm text-blue-700 dark:text-blue-300'>
            Loading suggestions...
          </span>
        </div>
      </div>
    );
  }

  if (suggestions.length === 0) return null;

  return (
    <div className='rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950'>
      <div className='flex items-center justify-between'>
        <button
          type='button'
          className='flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300 hover:underline'
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <Sparkles className='h-4 w-4' />
          Create from existing group ({suggestions.length} available)
        </button>
        <Button
          variant='ghost'
          size='sm'
          className='h-6 w-6 p-0 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200'
          onClick={onDismiss}
          title='Dismiss suggestions'
        >
          <X className='h-3 w-3' />
        </Button>
      </div>

      {!isCollapsed && (
        <div className='mt-3 space-y-1'>
          {suggestions.map((suggestion) => (
            <button
              type='button'
              key={suggestion.sensorGroupId}
              className='flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-blue-100 dark:hover:bg-blue-900'
              onClick={() =>
                onSelectSuggestion(suggestion.sensorGroupId, suggestion.sensorGroupName)}
            >
              <div className='flex items-center gap-2'>
                <span className='font-medium'>{suggestion.sensorGroupName}</span>
                {suggestion.waterscopeGroupType && (
                  <span className='text-xs text-muted-foreground'>
                    ({suggestion.waterscopeGroupType})
                  </span>
                )}
              </div>
              <span className='text-xs text-muted-foreground'>
                {suggestion.sensorCount} sensors
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
